/**
 * usePresenceHeartbeat
 * --------------------
 * Fires POST /api/presence/heartbeat every 60s while the tab is visible
 * and a user is signed in. Also fires immediately on mount, on visibility
 * change (returning to the tab), and on route change so admins see users
 * light up the moment they interact.
 *
 * Zero-config: mount this hook once inside AuthProvider (or any
 * component that lives above ALL authenticated routes). It automatically
 * no-ops when there is no user.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

const HEARTBEAT_INTERVAL_MS = 60 * 1000; // 60s cadence

export function usePresenceHeartbeat(user) {
    const location = useLocation();
    const timerRef = useRef(null);
    const lastPingRef = useRef(0);

    useEffect(() => {
        if (!user?.id) return; // signed-out — do nothing

        const send = () => {
            const now = Date.now();
            // Throttle: never ping twice within 10s (guards against
            // rapid nav bursts or visibility flapping).
            if (now - lastPingRef.current < 10_000) return;
            lastPingRef.current = now;
            api.post("/presence/heartbeat", {
                route: location.pathname,
                user_agent: navigator.userAgent?.slice(0, 200),
            }).catch(() => {
                // Silent — presence is best-effort, never disrupt UX.
            });
        };

        // 1) Fire once on mount / login / route change.
        send();

        // 2) Regular cadence while tab is open.
        timerRef.current = setInterval(() => {
            if (document.visibilityState === "visible") send();
        }, HEARTBEAT_INTERVAL_MS);

        // 3) Immediate ping the moment the tab regains focus, so a
        //    returning user flips from "idle" back to "online" instantly.
        const onVisibility = () => {
            if (document.visibilityState === "visible") send();
        };
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [user?.id, location.pathname]);
}
